import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, updateProfile } from "firebase/auth";
import { setDoc, doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import Home from "./Home";
import Services from "./Services";
import Contact from "./Contact";
import About from "./About";
import Auth from "./Auth";
import Account from "./Account";
import Payment from "./Payment";
import Appointment from "./Appointment";
import AppointmentTrade from "./AppointmentTrade";
import CompleteTrade from "./CompleteTrade";
import { applyRouteViewMode } from "./viewMode";
import ContactCompletion from "./ContactCompletion";
import Admin from "./Admin";
import AdminAuth from "./AdminAuth";
import JulySpecialBanner from "./JulySpecialBanner";

import ScrollToTop from "./ScrollToTop";
import './App.css';

function AuthWrapper() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleAuthSuccess = () => {
    const redirectPath = location.state?.redirectPath;

    if (redirectPath === '/account') {
      navigate('/account');
    } else if (redirectPath === '/appointment-swap') {
      navigate('/appointment-swap');
    } else if (redirectPath === '/complete-trade') {
      navigate('/complete-trade');
    } else if (redirectPath === '/services') {
      navigate('/services');
    } else {
      navigate('/services');
    }
  };

  return <Auth onAuthSuccess={handleAuthSuccess} />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser ? 'User logged in' : 'No user');
      if (firebaseUser) {
        console.log('User details:', firebaseUser.email, firebaseUser.uid);
        
        // Profile completion check removed
        // Email/password users provide area and contact during signup
        // Google users can add their contact info later from Account page if needed
      } else {
        console.log('No user - checking if this is a sign out or auth issue');
        // Check if user was actually signed out or if there's an auth issue
        console.log('Current auth state:', auth.currentUser);
      }
      setUser(firebaseUser);
      setLoading(false);
    });
    
    // Check if auth is already initialized
    const currentUser = auth.currentUser;
    if (currentUser) {
      console.log('Current user found on init:', currentUser.email, currentUser.uid);
      setUser(currentUser);
      setLoading(false);
    }
    
    return () => unsubscribe();
  }, []);

  function AccountWrapper(props) {
    const navigate = useNavigate();
    return <Account {...props} user={user} onSignOut={async () => { await signOut(auth); navigate('/auth'); }} />;
  }


  function AccountNavLink() {
    console.log('AccountNavLink - User state:', user, 'Loading:', loading);
    
    const handleAccountClick = () => {
      console.log('Account link clicked - User:', user, 'Loading:', loading);
      if (!user && !loading) {
        console.log('No user detected, checking auth state...');
        console.log('Current auth user:', auth.currentUser);
      }
      
      // Clean up appointment session if it exists (use force cleanup for more reliability)
      if (window.forceCleanupAppointmentSession) {
        window.forceCleanupAppointmentSession();
      } else if (window.cleanupAppointmentSession) {
        window.cleanupAppointmentSession();
      }
    };
    
    return (
      <Link
        to="/account"
        onClick={handleAccountClick}
        className="btn-responsive"
        style={{
          color: '#fff',
          textDecoration: 'none',
          fontWeight: '500',
          fontFamily: 'Arial, sans-serif',
          background: 'transparent',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseOver={e => {
          e.target.style.background = '#1f2937';
          e.target.style.color = '#f97316';
          e.target.style.transform = 'translateY(-2px)';
        }}
        onMouseOut={e => {
          e.target.style.background = 'transparent';
          e.target.style.color = '#fff';
          e.target.style.transform = 'translateY(0)';
        }}
        aria-label="My Account"
      >
        My Account
      </Link>
    );
  }


  // Profile completion removed - users provide area and contact during signup
  // Google users can complete their profile later from Account page if needed

  function RouteViewModeSync() {
    const location = useLocation();
    useEffect(() => {
      applyRouteViewMode(location.pathname);
    }, [location.pathname]);
    return null;
  }

  return (
    <Router>
      <ScrollToTop />
      <RouteViewModeSync />
      <div>
        <JulySpecialBanner />
        {!window.location.pathname.startsWith('/adminpfy9665k01') && (
        <nav className="nav-responsive">
          <Link 
            to="/" 
            className="text-responsive"
            style={{ 
              fontWeight: 'bold', 
              fontSize: 'clamp(1.2rem, 4vw, 1.6rem)', 
              color: '#1f2937',
              fontFamily: 'Brush Script MT, cursive',
              letterSpacing: '1px',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}
            onClick={() => {
              // Clean up appointment session if it exists (use force cleanup for more reliability)
              if (window.forceCleanupAppointmentSession) {
                window.forceCleanupAppointmentSession();
              } else if (window.cleanupAppointmentSession) {
                window.cleanupAppointmentSession();
              }
            }}
            onMouseOver={(e) => e.target.style.color = '#fff'}
            onMouseOut={(e) => e.target.style.color = '#1f2937'}
          >
            <img 
              src={`/images/logo.png?v=${Date.now()}`} 
              alt="Rocket Express Auto Wash Logo" 
              style={{
                height: 'clamp(35px, 8vw, 50px)',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <span>Rocket Express Auto Wash TT</span>
          </Link>
          <div className="nav-links">
            <Link 
              to="/" 
              className="btn-responsive"
              style={{ 
                color: '#fff', 
                textDecoration: 'none', 
                fontWeight: '500', 
                fontFamily: 'Arial, sans-serif',
                background: 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => {
                // Clean up appointment session if it exists
                if (window.cleanupAppointmentSession) {
                  window.cleanupAppointmentSession();
                }
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#1f2937';
                e.target.style.color = '#f97316';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#fff';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className="btn-responsive"
              style={{ 
                color: '#fff', 
                textDecoration: 'none', 
                fontWeight: '500', 
                fontFamily: 'Arial, sans-serif',
                background: 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => {
                // Clean up appointment session if it exists (use force cleanup for more reliability)
                if (window.forceCleanupAppointmentSession) {
                  window.forceCleanupAppointmentSession();
                } else if (window.cleanupAppointmentSession) {
                  window.cleanupAppointmentSession();
                }
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#1f2937';
                e.target.style.color = '#f97316';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#fff';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              About Us
            </Link>
            <Link 
              to="/services" 
              className="btn-responsive"
              style={{ 
                color: '#fff', 
                textDecoration: 'none', 
                fontWeight: '500', 
                fontFamily: 'Arial, sans-serif',
                background: 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => {
                // Clean up appointment session if it exists (use force cleanup for more reliability)
                if (window.forceCleanupAppointmentSession) {
                  window.forceCleanupAppointmentSession();
                } else if (window.cleanupAppointmentSession) {
                  window.cleanupAppointmentSession();
                }
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#1f2937';
                e.target.style.color = '#f97316';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#fff';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Make Appointment
            </Link>
{/* Appointment Trade feature temporarily hidden
            <Link 
              to="/appointment-swap" 
              className="btn-responsive"
              style={{ 
                color: '#fff', 
                textDecoration: 'none', 
                fontWeight: '500', 
                fontFamily: 'Arial, sans-serif',
                background: 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#1f2937';
                e.target.style.color = '#f97316';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#fff';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Appointment Trade
            </Link>
            */}
            <Link 
              to="/contact" 
              className="btn-responsive"
              style={{ 
                color: '#fff', 
                textDecoration: 'none', 
                fontWeight: '500', 
                fontFamily: 'Arial, sans-serif',
                background: 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => {
                // Clean up appointment session if it exists (use force cleanup for more reliability)
                if (window.forceCleanupAppointmentSession) {
                  window.forceCleanupAppointmentSession();
                } else if (window.cleanupAppointmentSession) {
                  window.cleanupAppointmentSession();
                }
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#1f2937';
                e.target.style.color = '#f97316';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#fff';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Contact
            </Link>
            <AccountNavLink />
          </div>
        </nav>
        )}
        <div style={{ padding: '0' }}>
          <Routes>
            <Route path="/" element={<Home user={user} />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services user={user} />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<AuthWrapper />} />
            <Route path="/account" element={
              loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <div>Loading...</div>
                </div>
              ) : user ? (
                <AccountWrapper />
              ) : (
                <AuthWrapper />
              )
            } />
            <Route path="/appointment" element={<Appointment />} />
    
            <Route path="/payment" element={<Payment />} />
            <Route path="/appointment-swap" element={<Navigate to="/" replace />} />
            <Route path="/complete-trade" element={<Navigate to="/" replace />} />
            <Route path="/adminpfy9665k01" element={<Admin />} />
            <Route path="/adminpfy9665k01-auth" element={<AdminAuth />} />
          </Routes>
        </div>
        
        <AppFooter />
      </div>
    </Router>
  );
}

function AppFooter() {
  const location = useLocation();
  if (location.pathname.startsWith('/adminpfy9665k01')) {
    return null;
  }

  const linkStyle = {
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    transition: 'color 0.2s'
  };

  return (
    <footer style={{
      background: '#232323',
      color: '#fff',
      padding: '2rem 1rem',
      marginTop: '3rem',
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
    }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
        gap: '2rem',
      }}>
        <nav style={{ display: 'flex', gap: '2rem', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Link to="/" style={linkStyle} onMouseOver={e => e.target.style.color = '#f97316'} onMouseOut={e => e.target.style.color = '#fff'}>Home</Link>
          <Link to="/about" style={linkStyle} onMouseOver={e => e.target.style.color = '#f97316'} onMouseOut={e => e.target.style.color = '#fff'}>About Us</Link>
          <Link to="/services" style={linkStyle} onMouseOver={e => e.target.style.color = '#f97316'} onMouseOut={e => e.target.style.color = '#fff'}>Make Appointment</Link>
          <Link to="/contact" style={linkStyle} onMouseOver={e => e.target.style.color = '#f97316'} onMouseOut={e => e.target.style.color = '#fff'}>Contact</Link>
          <Link
            to="/account"
            style={{
              ...linkStyle,
              borderRadius: '50%',
              padding: '0.3rem',
              background: 'rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseOver={e => { e.currentTarget.style.color = '#f97316'; e.currentTarget.style.background = '#1f2937'; }}
            onMouseOut={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            aria-label="Account"
          >
            <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
          </Link>
        </nav>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
          <a href="https://www.facebook.com/profile.php?id=61578589436624" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', fontSize: '1.5rem', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = '#f97316'} onMouseOut={e => e.currentTarget.style.color = '#fff'} aria-label="Facebook">
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
          </a>
          <a href="https://www.instagram.com/rocketexpresstt/" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', fontSize: '1.5rem', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = '#f97316'} onMouseOut={e => e.currentTarget.style.color = '#fff'} aria-label="Instagram">
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.266.069 1.646.069 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.266.058-1.646.069-4.85.069s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.241-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.775.131 4.602.425 3.635 1.392 2.668 2.359 2.374 3.532 2.315 4.809 2.256 6.089 2.243 6.498 2.243 12c0 5.502.013 5.911.072 7.191.059 1.277.353 2.45 1.32 3.417.967.967 2.14 1.261 3.417 1.32 1.28.059 1.689.072 7.191.072s5.911-.013 7.191-.072c1.277-.059 2.45-.353 3.417-1.32.967-.967 1.261-2.14 1.32-3.417.059-1.28.072-1.689.072-7.191s-.013-5.911-.072-7.191c-.059-1.277-.353-2.45-1.32-3.417C19.45.425 18.277.131 17 .072 15.721.013 15.311 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
          </a>
        </div>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{
          background: '#f97316',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '0.75rem 1.5rem',
          fontWeight: 'bold',
          fontSize: '1rem',
          cursor: 'pointer',
          transition: 'background 0.2s',
          marginLeft: 'auto',
        }}
        onMouseOver={e => e.target.style.background = '#ea580c'}
        onMouseOut={e => e.target.style.background = '#f97316'}
        >
          Back to Top
        </button>
      </div>
      <div style={{ textAlign: 'center', marginTop: '2rem', color: '#aaa', fontSize: '0.95rem' }}>
        &copy; {new Date().getFullYear()} Rocket Express Auto Wash TT. All rights reserved.
      </div>
    </footer>
  );
}

export default App;
